import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { XuiHostRequest, XuiServerConfig } from '../../../core/models/xui';
import { Button } from '../../../core/components/Button';
import { Input, Label, Select, Textarea } from '../../../core/components/FormElements';
import { Modal } from '../../../core/components/Modal';
import './AddXuiHostDialog.css';

interface AddXuiHostDialogProps {
    onClose: () => void;
    onConfirm: (hosts: XuiHostRequest | XuiHostRequest[]) => void;
    supportedTypes: string[];
    isLoading?: boolean;
    editMode?: boolean;
    initialData?: XuiServerConfig;
}

interface HostForm {
    identity: string;
    frendlyname: string;
    type: string;
    inboundId: string;
    host: string;
    user: string;
    password: string;
    token: string;
    useBearer: boolean;
}

const emptyForm: HostForm = {
    identity: '',
    frendlyname: '',
    type: '',
    inboundId: '',
    host: '',
    user: '',
    password: '',
    token: '',
    useBearer: false,
};

export const AddXuiHostDialog: React.FC<AddXuiHostDialogProps> = ({
    onClose,
    onConfirm,
    supportedTypes,
    isLoading,
    editMode = false,
    initialData,
}) => {
    const [form, setForm] = useState<HostForm>(() => {
        if (editMode && initialData) {
            const token = initialData.token || '';
            return {
                identity: initialData.identity || '',
                frendlyname: initialData.frendlyname || '',
                type: initialData.type || supportedTypes[0] || '',
                inboundId: String(initialData.inboundId ?? ''),
                host: initialData.host || '',
                user: initialData.user || '',
                password: initialData.password || '',
                token,
                useBearer: Boolean(token),
            };
        }
        return emptyForm;
    });
    const [error, setError] = useState<string | null>(null);
    const [csvMode, setCsvMode] = useState(false);
    const [csvInput, setCsvInput] = useState('');

    const parseCSV = (csv: string): XuiHostRequest[] => {
        const lines = csv.trim().split('\n').filter(line => line.trim());
        const result: XuiHostRequest[] = [];

        for (const line of lines) {
            const parts = line.split(',').map(p => p.trim());
            if (parts.length < 7) continue;

            const [identity, frendlyname, type, inboundId, host, user, password, token] = parts;
            const parsedInboundId = parseInt(inboundId, 10);
            const tokenValue = token || '';
            const hasToken = Boolean(tokenValue);

            if (!frendlyname || !type || isNaN(parsedInboundId) || !host) {
                continue;
            }
            if (!hasToken && (!user || !password)) {
                continue;
            }

            result.push({
                identity: identity || '',
                frendlyname,
                type: supportedTypes.includes(type) ? type : supportedTypes[0] || 'v2ray',
                inboundId: parsedInboundId,
                host,
                user: user || '',
                password: password || '',
                token: tokenValue,
            });
        }

        return result;
    };

    const validateCSV = (): boolean => {
        const hosts = parseCSV(csvInput);
        if (hosts.length === 0) {
            setError('Не удалось распознать ни одной записи. Проверьте формат CSV.');
            return false;
        }
        setError(null);
        return true;
    };

    const handleCSVConfirm = () => {
        if (!validateCSV()) return;
        const hosts = parseCSV(csvInput);
        onConfirm(hosts);
        onClose();
    };

    useEffect(() => {
        if (!editMode && supportedTypes.length > 0 && !form.type) {
            setForm(prev => ({ ...prev, type: supportedTypes[0] }));
        }
    }, [supportedTypes, editMode, form.type]);

    const validate = (): boolean => {
        if (!form.frendlyname.trim()) { setError('Название обязательно'); return false; }
        if (!form.type) { setError('Тип обязателен'); return false; }
        if (!form.inboundId || isNaN(parseInt(form.inboundId, 10))) { setError('Inbound ID обязателен и должен быть числом'); return false; }
        if (!form.host.trim()) { setError('Хост обязателен'); return false; }
        if (form.useBearer) {
            if (!form.token.trim()) { setError('Bearer token обязателен для 3x-ui ≥ 3.0.0'); return false; }
        } else {
            if (!form.user.trim()) { setError('Пользователь обязателен'); return false; }
            if (!form.password.trim()) { setError('Пароль обязателен'); return false; }
        }
        if (!form.identity.trim()) { setError('Identity обязателен'); return false; }
        setError(null);
        return true;
    };

    const handleConfirm = () => {
        if (!validate()) return;
        const host: XuiHostRequest = {
            identity: form.identity.trim(),
            frendlyname: form.frendlyname.trim(),
            type: form.type,
            inboundId: parseInt(form.inboundId, 10),
            host: form.host.trim(),
            user: form.useBearer ? '' : form.user.trim(),
            password: form.useBearer ? '' : form.password,
            token: form.useBearer ? form.token.trim() : '',
        };
        onConfirm([host]);
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) handleConfirm();
    };

    return (
        <Modal onClose={onClose} className="axh-dialog">
            <div className="axh-header">
                <h3>{editMode ? 'Редактировать XUI хост' : 'Добавить XUI хост'}</h3>
                <Button variant="ghost" size="sm" iconOnly icon={<X size={16} />} onClick={onClose} aria-label="Закрыть" />
            </div>

            <div className="axh-mode-toggle">
                <Button
                    type="button"
                    variant={!csvMode ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => { setCsvMode(false); setError(null); }}
                >
                    Один хост
                </Button>
                <Button
                    type="button"
                    variant={csvMode ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => { setCsvMode(true); setError(null); }}
                >
                    CSV
                </Button>
            </div>

            {csvMode ? (
                <div className="axh-form">
                    <div className="axh-csv-help">
                        Формат: <code>identity,название,тип,inbound_id,хост,пользователь,пароль[,token]</code>
                        <div>token обязателен для 3x-ui ≥ 3.0.0 (в этом случае пользователь/пароль можно оставить пустыми)</div>
                    </div>
                    <div className="axh-field">
                        <Label>CSV данные</Label>
                        <Textarea
                            value={csvInput}
                            onChange={e => setCsvInput(e.target.value)}
                            placeholder={`x3-fi-1vds,🇫🇮 Финляндия,3x-ui,0,192.168.1.1,admin,password,\nx3-se-1vds,🇸🇪 Швеция,3x-ui,0,192.168.1.2,,,bearer-token-value`}
                            rows={8}
                        />
                    </div>
                    {error && <div className="axh-error">{error}</div>}
                </div>
            ) : (
                <div className="axh-form" onKeyDown={handleKeyDown}>
                    <div className="axh-field">
                        <Label>Identity</Label>
                        <Input
                            type="text"
                            value={form.identity}
                            onChange={e => setForm(prev => ({ ...prev, identity: e.target.value }))}
                            placeholder="x3-fi-1vds"
                        />
                    </div>
                    <div className="axh-field">
                        <Label>Название</Label>
                        <Input
                            type="text"
                            value={form.frendlyname}
                            onChange={e => setForm(prev => ({ ...prev, frendlyname: e.target.value }))}
                            placeholder="🇫🇮 Финляндия"
                        />
                    </div>
                    <div className="axh-field">
                        <Label>Тип</Label>
                        <Select
                            value={form.type}
                            onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                        >
                            {supportedTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </Select>
                    </div>
                    <div className="axh-field">
                        <Label>Inbound ID</Label>
                        <Input
                            type="number"
                            value={form.inboundId}
                            onChange={e => setForm(prev => ({ ...prev, inboundId: e.target.value }))}
                            placeholder="0"
                        />
                    </div>
                    <div className="axh-field">
                        <Label>Хост</Label>
                        <Input
                            type="text"
                            value={form.host}
                            onChange={e => setForm(prev => ({ ...prev, host: e.target.value }))}
                            placeholder="192.168.1.1 или domain.com"
                        />
                    </div>
                    <div className="axh-field axh-checkbox-field">
                        <label className="axh-checkbox-label">
                            <input
                                type="checkbox"
                                checked={form.useBearer}
                                onChange={e => setForm(prev => ({ ...prev, useBearer: e.target.checked }))}
                            />
                            <span>Версия 3x-ui ≥ 3.0.0 (Bearer token)</span>
                        </label>
                    </div>
                    {form.useBearer ? (
                        <div className="axh-field">
                            <Label>Bearer Token</Label>
                            <Input
                                type="password"
                                value={form.token}
                                onChange={e => setForm(prev => ({ ...prev, token: e.target.value }))}
                                placeholder="••••••••"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="axh-field">
                                <Label>Пользователь</Label>
                                <Input
                                    type="text"
                                    value={form.user}
                                    onChange={e => setForm(prev => ({ ...prev, user: e.target.value }))}
                                    placeholder="admin"
                                />
                            </div>
                            <div className="axh-field">
                                <Label>Пароль</Label>
                                <Input
                                    type="password"
                                    value={form.password}
                                    onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                                    placeholder="••••••••"
                                />
                            </div>
                        </>
                    )}
                    {error && <div className="axh-error">{error}</div>}
                </div>
            )}

            <div className="axh-actions">
                <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                    Отмена
                </Button>
                {csvMode ? (
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleCSVConfirm}
                        disabled={isLoading || !csvInput.trim()}
                        loading={isLoading}
                    >
                        Добавить {parseCSV(csvInput).length || 0} хостов
                    </Button>
                ) : (
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={isLoading}
                        loading={isLoading}
                    >
                        {editMode ? 'Сохранить' : 'Добавить'}
                    </Button>
                )}
            </div>
        </Modal>
    );
};
